import { getWishlist, removeItem, onWishlistChange } from '@theme/wishlist-store';

// <s-wishlist-page> renders saved products by fetching each handle's product JSON.
// data-products-url comes from Liquid routes so no storefront path is hardcoded here.
class SWishlistPage extends HTMLElement {
  #ac;
  #grid;
  #empty;
  #cache = new Map();

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#grid = this.querySelector('[data-wishlist-grid]');
    this.#empty = this.querySelector('[data-wishlist-empty]');
    if (!this.#grid || !this.#empty) return;

    this.addEventListener('command', (event) => {
      if (event.command === '--remove' && event.source?.dataset.handle) removeItem(event.source.dataset.handle);
    }, { signal });

    onWishlistChange(() => this.#render(), signal);
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #fetchProduct(handle) {
    if (this.#cache.has(handle)) return this.#cache.get(handle);
    const request = fetch(`${this.dataset.productsUrl}${encodeURIComponent(handle)}.js`, { signal: this.#ac.signal })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    this.#cache.set(handle, request);
    return request;
  }

  #formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency: this.dataset.currency || 'INR' }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  async #render() {
    const handles = getWishlist();
    this.#empty.hidden = handles.length > 0;
    this.#grid.hidden = handles.length === 0;
    if (!handles.length) {
      this.#grid.replaceChildren();
      return;
    }
    this.setAttribute('aria-busy', 'true');
    const products = await Promise.all(handles.map((handle) => this.#fetchProduct(handle)));
    const cards = handles.map((handle, i) => this.#card(handle, products[i]));
    this.#grid.replaceChildren(...cards);
    this.removeAttribute('aria-busy');
  }

  #card(handle, product) {
    const item = document.createElement('li');
    item.className = 'relative flex flex-col gap-2';

    const media = document.createElement('a');
    media.className = 'product-shine relative block aspect-square overflow-hidden rounded-card bg-surface-secondary';
    const info = document.createElement('div');
    info.className = 'flex flex-col gap-0.5';

    if (product) {
      media.href = product.url;
      media.setAttribute('aria-hidden', 'true');
      media.tabIndex = -1;
      if (product.featured_image) {
        const img = document.createElement('img');
        const src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
        const url = new URL(src, location.href);
        url.searchParams.set('width', '600');
        img.src = url.toString();
        img.alt = '';
        img.loading = 'lazy';
        img.width = 600;
        img.height = 600;
        img.className = 'size-full object-cover motion-safe:transition-transform motion-safe:duration-300 hover:scale-[1.03]';
        media.append(img);
      }
      const title = document.createElement('a');
      title.href = product.url;
      title.className = 'type-body-sm font-medium text-on-surface no-underline line-clamp-2 hover:underline';
      title.textContent = product.title;
      const price = document.createElement('p');
      price.className = 'text-sm font-medium text-on-surface';
      price.textContent = this.#formatMoney(product.price);
      info.append(title, price);
    } else {
      const note = document.createElement('p');
      note.className = 'text-sm text-on-surface-secondary';
      note.textContent = this.dataset.unavailable || '';
      info.append(note);
    }

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.setAttribute('commandfor', this.id);
    remove.setAttribute('command', '--remove');
    remove.dataset.handle = handle;
    remove.className = 'self-start text-xs text-on-surface-secondary underline underline-offset-2 cursor-pointer hover:text-on-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';
    remove.textContent = this.dataset.removeLabel || '';
    if (product) remove.setAttribute('aria-label', `${this.dataset.removeLabel}: ${product.title}`);

    item.append(media, info, remove);
    return item;
  }
}

if (!customElements.get('s-wishlist-page')) customElements.define('s-wishlist-page', SWishlistPage);
